/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useRef, useState} from "react"
import {useTranslation} from "react-i18next"
import {Row, Col} from "antd"
import cx from "classnames"
import {studentService} from "services"
import styles from "./StatsChecklist.module.css"
import {handleError} from "helpers"
import {BaseButton} from "components/buttons"
import {useHistory} from "react-router-dom"
import {BaseDepartmentId} from "types/departments"
import {BaseNewFilter} from "uiKit"
import {FilterKey} from "types/filter"
import {useAdvisors, useCurrentTerms, useFilterStorage, useModel} from "hooks"
import CampusSelect from "components/CampusSelect"
import TermSelect from "components/TermSelect"
import ProgramSelect from "components/ProgramSelect"
import {NewStudentStatusSelectFilter} from "components/NewStudentStatusSelect"
import {KlassDropdown} from "components/Select"
import {UserOptionLabel} from "components/UserSelect/UserSelect"
import {BaseLoading} from "components"
import {BaseRangePicker} from "components/DateTimePicker"
import moment from "moment"

export enum StatsChecklistTitle {
    financialAidChecklist = "financialAidChecklist",
    admissionChecklist = "admissionChecklist",
    studentServiceChecklist = "studentServiceChecklist"
}

const BackUrlFromModule = {
    [BaseDepartmentId.Academics]: "academics/registrar/staff?tab=workspace",
    [BaseDepartmentId.Admissions]: "admissions/staff?tab=1"
}

type StatsChecklistProps = {
    departmentId: number
    title: StatsChecklistTitle
}

const INITIAL_FILTER = {
    campuses: [],
    terms: [],
    programs: [],
    statuses: [],
    admAdvisors: [],
    faAdvisors: [],
    acadAdvisors: [],
    startDateRange: null,
    projectedEndDateRange: null
}

export function StatsChecklist(props: StatsChecklistProps) {
    const {departmentId} = props
    const history = useHistory()
    const [summary, setSummary] = useState({
        completion: 0,
        pendingItems: 0,
        pendingStudents: 0,
        totalStudents: 0
    })

    const {t} = useTranslation(["common"])
    const {activeTerms} = useCurrentTerms()
    const isAcademics = departmentId === BaseDepartmentId.Academics

    const model = useModel()
    const defaultFilter = useMemo(() => {
        return {
            ...INITIAL_FILTER,
            campuses: (model.user?.profiles || []).map((profile) => profile.campuses || []).flat(),
            terms: activeTerms
        }
    }, [model.user, activeTerms])

    const allUserCampusIds = useMemo(() => {
        if (!model.isStaff()) {
            return []
        }
        const userCampuses = (model.user?.profiles || [])
            .map((profile) => (profile.campuses || []).map(({id}) => id))
            .filter(Boolean)

        return userCampuses.flat()
    }, [model])

    const {searchValue, currentFilter, setCurrentFilter, changeSearch, clearFilter, applyFilter} = useFilterStorage(
        FilterKey.StatsChecklist,
        defaultFilter
    )
    const {admissionAdvisors, finAidAdvisors, academicAdvisors} = useAdvisors().data || {}
    const dataSeq = React.useRef(0)
    const isMountedRef = useRef(true)
    const [isLoading, setLoading] = useState(false)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        getOverallSummary()
    }, [])

    const getFilter = () => {
        const {
            campuses,
            programs,
            terms,
            statuses,
            admAdvisors,
            faAdvisors,
            acadAdvisors,
            startDateRange,
            projectedEndDateRange
        } = currentFilter
        let filter: any = {}
        if (terms) {
            filter.termsIds = terms.map(({id}) => id)
        }
        if (campuses) {
            filter.campusIds = campuses.map(({id}) => id)
        }
        if (programs) {
            filter.programIds = programs.map(({id}) => id)
        }
        if (statuses) {
            filter.statusIds = statuses.map(({statusId}) => statusId)
        }
        if (admAdvisors && departmentId === BaseDepartmentId.Admissions) {
            filter.advisorProfileIds = (admAdvisors || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (faAdvisors && departmentId === BaseDepartmentId.FinancialAid) {
            filter.advisorProfileIds = (faAdvisors || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (acadAdvisors && ![BaseDepartmentId.Admissions, BaseDepartmentId.FinancialAid].includes(departmentId)) {
            filter.advisorProfileIds = (acadAdvisors || []).map((item) => item?.profileId).filter((item) => item)
        }
        if (startDateRange) {
            filter.startDateRange = [
                moment(startDateRange[0]).startOf("date").toISOString(),
                moment(startDateRange[1]).endOf("date").toISOString()
            ]
        }
        if (projectedEndDateRange) {
            filter.projectedEndDateRange = [
                moment(projectedEndDateRange[0]).startOf("date").format("YYYY-MM-DD"),
                moment(projectedEndDateRange[1]).endOf("date").format("YYYY-MM-DD")
            ]
        }

        return filter
    }

    const getOverallSummary = async () => {
        try {
            dataSeq.current += 1
            const currentSeq = dataSeq.current
            if (isMountedRef.current) setLoading(true)
            const filter = getFilter()
            const summary = await studentService.getStudentChecklistListOverallSummary({
                departmentId: props.departmentId,
                ...filter
            })
            if (dataSeq.current !== currentSeq) return
            if (isMountedRef.current) setSummary(summary)
        } catch (error) {
            if (isMountedRef.current) handleError(error)
        } finally {
            if (isMountedRef.current) setLoading(false)
        }
    }

    const redirectToChecklistReport = () => {
        history.push(`/reports/scrub-reports?departmentId=${props.departmentId}`, {
            backUrl: BackUrlFromModule[props.departmentId]
        })
    }

    const onApplyFilter = () => {
        applyFilter()
        getOverallSummary()
    }

    const onClearFilter = () => {
        setCurrentFilter({})
        clearFilter()
    }

    const renderAdvisorFilter = () => {
        if (departmentId === BaseDepartmentId.Admissions) {
            return (
                <KlassDropdown
                    isMulti
                    options={admissionAdvisors}
                    valueKey="profileId"
                    labelKey="fullName"
                    getOptionLabel={UserOptionLabel}
                    placeholder={t("statsFilter.admissionAdvisor")}
                    value={currentFilter.admAdvisors}
                    onChange={(newValue) => setCurrentFilter({...currentFilter, admAdvisors: newValue})}
                />
            )
        }
        if (departmentId === BaseDepartmentId.FinancialAid) {
            return (
                <KlassDropdown
                    isMulti
                    options={finAidAdvisors}
                    valueKey="profileId"
                    labelKey="fullName"
                    getOptionLabel={UserOptionLabel}
                    placeholder={t("statsFilter.financialAidAdvisor")}
                    value={currentFilter.faAdvisors}
                    onChange={(newValue) => setCurrentFilter({...currentFilter, faAdvisors: newValue})}
                />
            )
        }

        return (
            <KlassDropdown
                isMulti
                options={academicAdvisors}
                valueKey="profileId"
                labelKey="fullName"
                getOptionLabel={UserOptionLabel}
                placeholder={t("statsFilter.academicAdvisor")}
                value={currentFilter.acadAdvisors}
                onChange={(newValue) => setCurrentFilter({...currentFilter, acadAdvisors: newValue})}
            />
        )
    }

    return (
        <div className={styles.wrap}>
            <div className={styles.headerWrap}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerLeftTitleWrap}>
                        <p className={styles.headerLeft__title}>{t(`statsChecklist.${props.title}`).toUpperCase()}</p>
                    </div>
                </div>
            </div>
            <BaseNewFilter
                filter={currentFilter}
                className={styles.filterContainer}
                showSearch={false}
                searchValue={searchValue}
                onSearchInput={changeSearch}
                onClick={onApplyFilter}
                onClickClear={onClearFilter}
                renderRightFilter={() => (
                    <div className={styles.actionWrap}>
                        <BaseButton
                            title={t("common:action.report")}
                            variant="secondary"
                            onClick={redirectToChecklistReport}
                        />
                    </div>
                )}>
                <Row gutter={[40, 32]}>
                    <Col span={12}>
                        <CampusSelect
                            isMulti
                            placeholder={t("statsFilter.campus")}
                            value={currentFilter?.campuses}
                            onChange={(newValue) => setCurrentFilter({...currentFilter, campuses: newValue})}
                            displayOnlyCampusIds={allUserCampusIds}
                        />
                    </Col>
                    <Col span={12}>
                        <TermSelect
                            isMulti
                            placeholder={t("statsFilter.startingTerm")}
                            value={currentFilter.terms}
                            onChange={(newValue) => setCurrentFilter({...currentFilter, terms: newValue})}
                        />
                    </Col>
                    <Col span={12}>
                        <ProgramSelect
                            isMulti
                            placeholder={t("statsFilter.program")}
                            value={currentFilter.programs}
                            onChange={(newValue) => setCurrentFilter({...currentFilter, programs: newValue})}
                        />
                    </Col>
                    <Col span={12}>
                        <NewStudentStatusSelectFilter
                            isMulti
                            value={currentFilter.statuses}
                            onChange={(newValue) => setCurrentFilter({...currentFilter, statuses: newValue})}
                        />
                    </Col>
                    <Col span={12}>{renderAdvisorFilter()}</Col>
                    <Col span={12}>
                        <BaseRangePicker
                            placeholder={["Start Date From", "To"]}
                            value={
                                currentFilter.startDateRange
                                    ? [moment(currentFilter.startDateRange[0]), moment(currentFilter.startDateRange[1])]
                                    : null
                            }
                            onChange={(value) => {
                                if (value) {
                                    setCurrentFilter({
                                        ...currentFilter,
                                        startDateRange: [moment(value[0]), moment(value[1])]
                                    })
                                } else {
                                    setCurrentFilter({...currentFilter, startDateRange: null})
                                }
                            }}
                        />
                    </Col>
                    {isAcademics && (
                        <Col span={12}>
                            <BaseRangePicker
                                placeholder={["Projected End Date From", "To"]}
                                value={
                                    currentFilter.projectedEndDateRange
                                        ? [
                                              moment(currentFilter.projectedEndDateRange[0]),
                                              moment(currentFilter.projectedEndDateRange[1])
                                          ]
                                        : null
                                }
                                onChange={(value) => {
                                    if (value) {
                                        setCurrentFilter({
                                            ...currentFilter,
                                            projectedEndDateRange: [moment(value[0]), moment(value[1])]
                                        })
                                    } else {
                                        setCurrentFilter({...currentFilter, projectedEndDateRange: null})
                                    }
                                }}
                            />
                        </Col>
                    )}
                </Row>
            </BaseNewFilter>
            <div className={styles.dataContainer}>
                <Row gutter={[24, 24]}>
                    <Col span={8}>
                        <div className={styles.cardItem}>
                            <p className={styles.cardItem__number}>{summary.totalStudents}</p>
                            <p className={styles.cardItem__title}>{t("statsChecklist.students").toUpperCase()}</p>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div className={styles.cardItem}>
                            <p className={styles.cardItem__number}>{Math.ceil(summary.completion * 100)}%</p>
                            <p className={styles.cardItem__title}>{t("statsChecklist.completion").toUpperCase()}</p>
                        </div>
                    </Col>
                    <Col span={8}>
                        <div className={cx(styles.cardItem, styles.cardItem__pending)}>
                            <div className={styles.pendingWrap}>
                                <div className={styles.pendingCard}>
                                    <p className={styles.pendingCard__number}>{summary.pendingStudents}</p>
                                    <p className={styles.pendingCard__title}>
                                        {t("statsChecklist.students").toUpperCase()}
                                    </p>
                                </div>
                                <div className={styles.pendingCard}>
                                    <p className={styles.pendingCard__number}>{summary.pendingItems}</p>
                                    <p className={styles.pendingCard__title}>
                                        {t("statsChecklist.items").toUpperCase()}
                                    </p>
                                </div>
                            </div>
                            <p className={styles.pending__title}>{t("statsChecklist.overdue").toUpperCase()}</p>
                        </div>
                    </Col>
                </Row>
                <BaseLoading isShow={isLoading} />
            </div>
        </div>
    )
}
